import { useEffect, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { intelligenceSettings } from '@cwos/api-client';
import { ConsequenceNote, ErrorPanel, SkeletonSet } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { SettingsCard, SettingsHeader } from './common.js';
import { LoadFailure } from '../LoadFailure.js';

type Setting = intelligenceSettings.ExecutionSetting;
type Mode = intelligenceSettings.ExecutionMode;

const MODE_LABEL: Record<Mode, string> = {
  plan: 'Plan — proposes the steps, never carries them out',
  manual: 'Manual — asks the person to confirm each change',
  auto: 'Auto — routine changes without asking; higher-risk ones still ask',
  bypass: 'Bypass — no Intelligence confirmation',
  off: 'Off — Intelligence makes no changes here',
};

const BYPASS_COPY =
  'Bypass skips Intelligence’s own confirmation step and nothing else. Intelligence can still only ' +
  'do what that person could already do in Phibian by hand: their permissions, your organization’s ' +
  'boundaries, signed records and platform safety controls all still apply.';

/**
 * Settings → Intelligence: how Phibian Intelligence carries out changes, as an
 * organization default, per action area, per role and per person. The most
 * specific setting wins; nothing set means Manual.
 */
export function IntelligenceSettings() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canManage = has('org:manage');
  const view = useQuery({
    queryKey: ['intelligence', 'settings'],
    queryFn: () => intelligenceSettings.read(),
  });
  const [draft, setDraft] = useState<Setting[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (view.data && draft === null) setDraft(view.data.settings);
  }, [view.data, draft]);

  const save = useMutation({
    mutationFn: (settings: Setting[]) => intelligenceSettings.save(settings),
    onSuccess: (next) => {
      setError(null);
      setDraft(next.settings);
      qc.setQueryData(['intelligence', 'settings'], next);
    },
    onError: (err: unknown) => setError(messageOf(err)),
  });

  if (view.isLoading || (view.data && draft === null)) {
    return <SkeletonSet surface="table" rows={4} />;
  }
  if (view.isError || !view.data || draft === null) {
    return (
      <LoadFailure
        error={view.error}
        message="We couldn’t load the Intelligence settings just now."
        onRetry={() => void view.refetch()}
      />
    );
  }
  const data = view.data;
  const orgDefault =
    draft.find((s) => s.scope === 'org' && s.domain === undefined)?.mode ?? 'manual';

  const replace = (match: (s: Setting) => boolean, next: Setting | null) =>
    setDraft([...draft.filter((s) => !match(s)), ...(next ? [next] : [])]);

  const domainSelect = (
    value: string,
    onChange: (domain: string) => void,
    label: string,
  ): ReactElement => (
    <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">All areas</option>
      {data.domains.map((d) => (
        <option key={d} value={d}>
          {d}
        </option>
      ))}
    </select>
  );
  const modeSelect = (
    value: Mode,
    onChange: (mode: Mode) => void,
    label: string,
    allowOff: boolean,
  ): ReactElement => (
    <select
      aria-label={label}
      value={value}
      disabled={!canManage}
      onChange={(e) => onChange(e.target.value as Mode)}
    >
      {[...data.modes, ...(allowOff ? (['off'] as const) : [])].map((m) => (
        <option key={m} value={m}>
          {MODE_LABEL[m]}
        </option>
      ))}
    </select>
  );

  const overrides = (scope: 'role' | 'user') => {
    const rows = draft.map((s, index) => ({ s, index })).filter(({ s }) => s.scope === scope);
    const who =
      scope === 'role' ? data.roles.map((r) => ({ id: r.key, name: r.name })) : data.users;
    const update = (index: number, next: Setting) =>
      setDraft(draft.map((s, i) => (i === index ? next : s)));
    return (
      <div style={{ display: 'grid', gap: 'var(--space-8)', marginTop: 'var(--space-12)' }}>
        {rows.length === 0 ? <p className="rn-muted">None.</p> : null}
        {rows.map(({ s, index }) => {
          const id = scope === 'role' ? s.roleKey : s.userId;
          const named = who.find((w) => w.id === id)?.name ?? id;
          return (
            <div key={index} style={{ display: 'flex', gap: 'var(--space-8)', flexWrap: 'wrap' }}>
              <strong style={{ minWidth: 160 }}>{named}</strong>
              {domainSelect(
                s.domain ?? '',
                (d) => {
                  const { domain: _drop, ...rest } = s;
                  const mode = d === '' && s.mode === 'off' ? 'manual' : s.mode;
                  update(
                    index,
                    d === ''
                      ? { ...rest, mode }
                      : { ...rest, mode, domain: d as intelligenceSettings.IntelligenceDomain },
                  );
                },
                `${named} area`,
              )}
              {modeSelect(
                s.mode,
                (mode) => update(index, { ...s, mode }),
                `${named} mode`,
                s.domain !== undefined,
              )}
              {canManage ? (
                <button
                  type="button"
                  className="rn-btn-quiet"
                  onClick={() => setDraft(draft.filter((_, i) => i !== index))}
                >
                  Remove
                </button>
              ) : null}
            </div>
          );
        })}
        {canManage ? (
          <select
            aria-label={scope === 'role' ? 'Add a role' : 'Add a person'}
            value=""
            onChange={(e) =>
              e.target.value &&
              setDraft([
                ...draft,
                scope === 'role'
                  ? { scope, roleKey: e.target.value, mode: 'manual' }
                  : { scope, userId: e.target.value, mode: 'manual' },
              ])
            }
          >
            <option value="">{scope === 'role' ? 'Add a role…' : 'Add a person…'}</option>
            {who.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>
    );
  };

  return (
    <div className="rn-col" style={{ maxWidth: 860 }}>
      <SettingsHeader
        title="Intelligence"
        blurb="How Phibian Intelligence carries out changes it is asked to make. The most specific setting wins; nothing set means Manual."
      />
      {error ? <ErrorPanel message={error} /> : null}

      <SettingsCard>
        <h2 className="rn-card-title">Organization default</h2>
        <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
          {modeSelect(
            orgDefault,
            (mode) =>
              replace((s) => s.scope === 'org' && s.domain === undefined, { scope: 'org', mode }),
            'Organization default mode',
            false,
          )}
          <ConsequenceNote text={BYPASS_COPY} />
        </div>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Areas</h2>
        <p className="rn-muted">Turn an area off, or give it its own mode.</p>
        <div style={{ display: 'grid', gap: 'var(--space-8)', marginTop: 'var(--space-12)' }}>
          {data.domains.map((domain) => {
            const current = draft.find((s) => s.scope === 'org' && s.domain === domain);
            return (
              <label
                key={domain}
                style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}
              >
                <span style={{ minWidth: 140 }}>{domain}</span>
                <select
                  aria-label={`${domain} mode`}
                  value={current?.mode ?? ''}
                  disabled={!canManage}
                  onChange={(e) =>
                    replace(
                      (s) => s.scope === 'org' && s.domain === domain,
                      e.target.value === ''
                        ? null
                        : { scope: 'org', domain, mode: e.target.value as Mode },
                    )
                  }
                >
                  <option value="">Use the default</option>
                  {[...data.modes, 'off' as const].map((m) => (
                    <option key={m} value={m}>
                      {MODE_LABEL[m]}
                    </option>
                  ))}
                </select>
              </label>
            );
          })}
        </div>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Roles</h2>
        <p className="rn-muted">A person with several roles gets the most cautious of them.</p>
        {overrides('role')}
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">People</h2>
        {overrides('user')}
      </SettingsCard>

      {canManage ? (
        <button
          type="button"
          className="signin-devbtn"
          style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
          disabled={save.isPending}
          onClick={() => save.mutate(draft)}
        >
          {save.isPending ? 'Saving…' : 'Save Intelligence settings'}
        </button>
      ) : null}
    </div>
  );
}

function messageOf(err: unknown): string {
  if (err !== null && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') return message;
  }
  return 'That did not save.';
}
